import {
  Component,
  signal,
  ChangeDetectionStrategy,
  computed,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { SortEvent, ConfirmationService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import {
  ColumnDef,
  EmptyStateComponent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
  TfColumnDefDirective,
  TfDataTableComponent,
  TfPageEvent,
} from '@tourfold/libraries/shared-ui';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SkillsService } from './skills.service';
import {
  Skill,
  SkillList,
  CreateSkill,
  UpdateSkill,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { nonEmptyOrUndefined, ServerFieldErrors } from '@tourfold/libraries/shared-util';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { provideSkillsAiPageContext } from './skills-list.ai-page-context';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

@Component({
  selector: 'tf-settings-skills-list',
  imports: [
    CommonModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    ConfirmDialogModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    EmptyStateComponent,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    TfDataTableComponent,
    TfColumnDefDirective,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './skills-list.html',
  styleUrl: './skills-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class Skills {
  private readonly skillsService = inject(SkillsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resourceAccessService = inject(ResourceAccessService);

  readonly canCreateSkill = computed(() =>
    this.resourceAccessService.canPerform('skills', 'create')
  );
  readonly canUpdateSkill = computed(() =>
    this.resourceAccessService.canPerform('skills', 'update')
  );
  readonly canDeleteSkill = computed(() =>
    this.resourceAccessService.canPerform('skills', 'delete')
  );
  private readonly searchSubject = new Subject<string>();
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly fb = inject(FormBuilder);

  readonly skills = signal<Skill[]>([]);
  readonly isLoading = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly isEditDialogVisible = signal<boolean>(false);
  readonly isCreateDialogVisible = signal<boolean>(false);
  readonly searchQuery = signal<string>('');
  readonly totalRecords = signal(0);
  readonly originalSkill = signal<Skill | null>(null);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal<number>(0);
  readonly pageSize = 10;

  readonly editSkillForm = this.fb.group({
    skill: new FormControl<string>('', [Validators.required]),
    skillType: new FormControl<string>('', [Validators.required]),
    name: new FormControl<string>('', [Validators.required]),
    shortKey: new FormControl<string>('', [Validators.required]),
  });

  readonly createSkillForm = this.fb.group({
    skill: new FormControl<string>('', [Validators.required]),
    skillType: new FormControl<string>('', [Validators.required]),
    name: new FormControl<string>('', [Validators.required]),
    shortKey: new FormControl<string>('', [Validators.required]),
  });

  // Server-side validation errors (populated from RFC 9457 ProblemDetail responses).
  readonly createServerErrors = new ServerFieldErrors(this.createSkillForm);
  readonly editServerErrors = new ServerFieldErrors(this.editSkillForm);

  readonly columns: ColumnDef[] = [
    { field: 'name', title: 'SETTINGS_SKILLS.NAME', type: 'text', sortable: true, width: '20%' },
    { field: 'skill', title: 'SETTINGS_SKILLS.SKILL', type: 'text', sortable: true, width: '20%' },
    {
      field: 'skillType',
      title: 'SETTINGS_SKILLS.SKILL_TYPE',
      type: 'text',
      sortable: true,
      width: '20%',
    },
    {
      field: 'shortKey',
      title: 'SETTINGS_SKILLS.SHORT_KEY',
      type: 'text',
      sortable: true,
      width: '20%',
    },
    { field: '_actions', title: 'SETTINGS_SKILLS.ACTIONS', type: 'custom' },
  ];

  readonly editableSkills = computed(() => this.skills());
  readonly showEmptyState = computed(
    () => this.hasLoadedOnce() && this.searchQuery().trim() === '' && this.totalRecords() === 0
  );

  constructor() {
    this.setupSearchDebounce();
    this.loadSkills();
    provideSkillsAiPageContext({
      skills: this.skills,
      searchQuery: this.searchQuery,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      originalSkill: this.originalSkill,
      isEditDialogVisible: this.isEditDialogVisible,
    });
  }

  private setupSearchDebounce() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        this.searchQuery.set(query);
        this.loadSkills(0, this.pageSize);
      });
  }

  private loadSkills(page = 0, size = this.pageSize) {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.actionError.set(null);

    const sortArray: string[] = [];
    const field = this.sortField();
    const order = this.sortOrder();
    if (field && order !== 0) {
      const backendField = this.mapFieldToBackend(field);
      const direction = order === 1 ? 'asc' : 'desc';
      sortArray.push(`${backendField}:${direction}`);
    }

    this.skillsService
      .getSkillsData({
        page,
        size,
        sort: sortArray.length > 0 ? sortArray : undefined,
        search: nonEmptyOrUndefined(this.searchQuery()),
      })
      .subscribe({
        next: (response: SkillList) => {
          this.skills.set(response.items);
          this.totalRecords.set(response.page.totalElements);
          this.isLoading.set(false);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const message = this.translateService.instant('SETTINGS.LOAD_ERROR.TITLE');
          if (hadLoadedOnce) {
            this.hasLoadedOnce.set(true);
            this.actionError.set(message);
          } else {
            this.loadError.set(message);
            this.initialLoadFailed.set(true);
            void this.updateLoadErrorDetails(error);
          }
        },
      });
  }

  private mapFieldToBackend(field: string): string {
    const fieldMap: Record<string, string> = {
      name: 'name',
      skill: 'skill',
      skillType: 'skill_type',
      shortKey: 'short_key',
    };
    return fieldMap[field] ?? field;
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 0;

    if (this.sortField() === newField && this.sortOrder() === newOrder) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);

    this.loadSkills(0, this.pageSize);
  }

  onPageChange(event: TfPageEvent): void {
    const page = Math.floor(event.first / event.rows);
    this.loadSkills(page, event.rows);
  }

  reloadSkills(): void {
    this.loadSkills(0, this.pageSize);
  }

  onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  editSkill(skill: Skill): void {
    this.originalSkill.set(skill);

    this.editSkillForm.patchValue({
      skill: skill.skill ?? '',
      skillType: skill.skillType ?? '',
      name: skill.name ?? '',
      shortKey: skill.shortKey ?? '',
    });
    this.editServerErrors.clear();

    setTimeout(() => {
      this.isEditDialogVisible.set(true);
    }, 0);
  }

  saveEditing(): void {
    if (this.editSkillForm.invalid) {
      this.editSkillForm.markAllAsTouched();
      return;
    }

    const original = this.originalSkill();
    if (!original) return;

    const formValue = this.editSkillForm.getRawValue();

    const originalWithLockVersion = original as Skill & {
      lockVersion?: number;
      lock_version?: number;
    };
    const lockVersion =
      originalWithLockVersion.lockVersion ?? originalWithLockVersion.lock_version ?? 0;

    const updateSkill: UpdateSkill = {
      lockVersion,
      skill: formValue.skill?.trim() ?? '',
      skillType: formValue.skillType?.trim() ?? '',
      name: formValue.name?.trim() ?? '',
      shortKey: formValue.shortKey?.trim() ?? '',
    };

    this.editServerErrors.clear();
    this.isLoading.set(true);
    this.skillsService.updateSkill(original.id, updateSkill).subscribe({
      next: (updatedSkill) => {
        const updatedSkills = this.skills().map((skill) =>
          skill.id === updatedSkill.id ? updatedSkill : skill
        );
        this.skills.set(updatedSkills);
        this.isLoading.set(false);
        this.closeEditDialog();
        this.loadSkills(0, this.pageSize);
      },
      error: async (error: unknown) => {
        this.isLoading.set(false);
        const applied = await this.editServerErrors.apply(error);
        if (applied && applied.fieldCount > 0) {
          if (applied.generalMessages.length > 0) {
            this.actionError.set(applied.generalMessages.join(' '));
          }
          return;
        }
        const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
        const fallback =
          joinedGeneral !== ''
            ? joinedGeneral
            : this.translateService.instant('SETTINGS_SKILLS.ERROR.SKILL_UPDATE_FAILED');
        this.actionError.set(fallback);
      },
    });
  }

  closeEditDialog(): void {
    this.isEditDialogVisible.set(false);
    this.editSkillForm.reset();
    this.originalSkill.set(null);
    this.editServerErrors.clear();
  }

  createSkill(): void {
    this.createSkillForm.reset({
      skill: '',
      skillType: '',
      name: '',
      shortKey: '',
    });
    this.createServerErrors.clear();
    this.isCreateDialogVisible.set(true);
  }

  saveNewSkill(): void {
    if (this.createSkillForm.invalid) {
      this.createSkillForm.markAllAsTouched();
      return;
    }
    const formValue = this.createSkillForm.getRawValue();
    const createSkill: CreateSkill = {
      skill: (formValue.skill ?? '').trim(),
      skillType: (formValue.skillType ?? '').trim(),
      name: (formValue.name ?? '').trim(),
      shortKey: (formValue.shortKey ?? '').trim(),
    };

    this.createServerErrors.clear();
    this.isLoading.set(true);
    this.skillsService.createSkill(createSkill).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.closeCreateDialog();
        this.loadSkills(0, this.pageSize);
      },
      error: async (error: unknown) => {
        this.isLoading.set(false);
        const applied = await this.createServerErrors.apply(error);
        if (applied && applied.fieldCount > 0) {
          if (applied.generalMessages.length > 0) {
            this.actionError.set(applied.generalMessages.join(' '));
          }
          return;
        }
        const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
        const fallback =
          joinedGeneral !== ''
            ? joinedGeneral
            : this.translateService.instant('SETTINGS_SKILLS.ERROR.SKILL_CREATE_FAILED');
        this.actionError.set(fallback);
      },
    });
  }

  closeCreateDialog(): void {
    this.isCreateDialogVisible.set(false);
    this.createSkillForm.reset({
      skill: '',
      skillType: '',
      name: '',
      shortKey: '',
    });
    this.createServerErrors.clear();
  }

  deleteSkill(skill: Skill): void {
    const translatedMessage = this.translateService.instant(
      'SETTINGS_SKILLS.DELETE_CONFIRM_MESSAGE'
    );
    const message = translatedMessage.replace('{name}', skill.name);

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_SKILLS.DELETE_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.skillsService.deleteSkill(skill.id).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.loadSkills(0, this.pageSize);
          },
          error: (error: unknown) => {
            this.isLoading.set(false);
            let errorMessage = this.translateService.instant(
              'SETTINGS_SKILLS.ERROR.SKILL_DELETE_FAILED'
            );
            if (error && typeof error === 'object' && 'error' in error) {
              const httpError = error as { error?: unknown; status?: number };
              if (httpError.error && typeof httpError.error === 'object') {
                const errorBody = httpError.error as Record<string, unknown>;
                if (errorBody.message && typeof errorBody.message === 'string') {
                  errorMessage = errorBody.message;
                }
              }
            }
            this.actionError.set(errorMessage);
          },
        });
      },
    });
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }
}
